/**
 * Which surface `/new` owes the user, ported from t3code's index-route ladder
 * (`apps/web/src/routes/_chat.index.tsx`): one card per diagnosis, each with a
 * single CTA, so the user is never asked to work out which button applies.
 */
export type NewThreadLandingSurface =
  | "loading"
  | "connect-environment"
  | "cloud-catalog-unavailable"
  | "composer";

export interface NewThreadLandingSurfaceInput {
  /**
   * The saved-environment registry rehydrates from persistence asynchronously
   * and reads zero connections for *everybody* until it lands, so deciding
   * before this is true shows the connect card to users who have connections.
   */
  readonly savedEnvironmentsHydrated: boolean;
  readonly savedEnvironmentCount: number;
  readonly connectedProjectCount: number;
  readonly cloudCatalogRowCount: number;
  readonly desktopInventoryProjectCount: number;
  readonly cloudCatalogIsLoading: boolean;
  readonly cloudCatalogHasError: boolean;
  /**
   * t3code gates its connect card on `hosted-static`. Forge's equivalent is the
   * cloud control surface: a desktop build always owns a local runtime, so "no
   * connections" is not a state a desktop user could act on.
   */
  readonly isCloudControlSurface: boolean;
}

export function resolveNewThreadLandingSurface(
  input: NewThreadLandingSurfaceInput,
): NewThreadLandingSurface {
  const projectCount =
    input.connectedProjectCount + input.cloudCatalogRowCount + input.desktopInventoryProjectCount;

  // Any project at all outranks every empty state, including while the catalog
  // refreshes over cached rows — the user has work, so give them the composer.
  if (projectCount > 0) return "composer";

  if (!input.savedEnvironmentsHydrated) return "loading";

  // Only wait on a catalog that can still change the answer. An errored fetch
  // never will, and holding "loading" for it renders nothing forever — a worse
  // failure than the flash the wait exists to prevent.
  if (input.cloudCatalogIsLoading && !input.cloudCatalogHasError) return "loading";

  // Nowhere to run outranks not being able to read the catalog: connecting is
  // the only useful next step either way.
  if (input.isCloudControlSurface && input.savedEnvironmentCount === 0) {
    return "connect-environment";
  }

  // An empty list after a FAILED read is not evidence the user has no projects,
  // so it must not be reported as one. Both cases used to land on the bare "Add
  // a project" prompt, which left someone whose catalog read had failed adding
  // nothing to fix a problem that was never theirs.
  if (input.cloudCatalogHasError) return "cloud-catalog-unavailable";

  // A connected environment is enough to compose the draft. Project choice
  // and the platform model offer both exist before the first project is added.
  return "composer";
}
